/**
 * ClaimAuthFlow — Authentication step for the claim flow.
 *
 * Handles two paths:
 * 1. Existing account: Login form with masked email hint
 * 2. New account: Signup form with name, email, password, confirm password, terms checkbox
 *
 * After successful auth, fires `onAuthenticated()` so the parent can proceed
 * with claiming the submission.
 */

import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  View,
  Text,
  TextInput,
  Pressable,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { Mail, Lock, Eye, EyeOff, User, ChevronLeft, Check } from 'lucide-react-native';
import Animated, { FadeInUp } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import { useAuth } from '@/lib/supabase';
import { handleError } from '@/lib/errors';
import { useAlert } from '@/components/AlertDialog';
import { useRouter } from 'expo-router';

// ============================================================================
// Types
// ============================================================================

interface ClaimAuthFlowProps {
  /** Masked email from token validation (e.g. "j***@example.com") */
  contactEmail: string;
  /** Whether this email already has an account */
  existingAccount: boolean;
  /** Callback when auth is complete */
  onAuthenticated: () => void;
  /** Callback to go back */
  onBack: () => void;
}

// ============================================================================
// Password Validation
// ============================================================================

function validatePassword(password: string): { valid: boolean; message: string } {
  if (password.length < 8) {
    return { valid: false, message: 'Password must be at least 8 characters long.' };
  }
  if (!/[A-Z]/.test(password)) {
    return { valid: false, message: 'Password must include at least one uppercase letter.' };
  }
  if (!/[a-z]/.test(password)) {
    return { valid: false, message: 'Password must include at least one lowercase letter.' };
  }
  if (!/[0-9]/.test(password)) {
    return { valid: false, message: 'Password must include at least one number.' };
  }
  return { valid: true, message: '' };
}

function sanitizeAuthError(rawMessage: string): string {
  const lower = rawMessage.toLowerCase();
  if (lower.includes('invalid login credentials') || lower.includes('invalid password')) {
    return 'Incorrect email or password.';
  }
  if (lower.includes('user already registered') || lower.includes('already exists')) {
    return 'An account with this email already exists. Try logging in instead.';
  }
  if (lower.includes('email not confirmed')) {
    return 'Please check your email to confirm your account.';
  }
  if (lower.includes('rate limit') || lower.includes('too many')) {
    return 'Too many attempts. Please wait a moment and try again.';
  }
  if (lower.includes('network') || lower.includes('fetch')) {
    return 'Network error. Please check your connection and try again.';
  }
  return 'Authentication failed. Please try again.';
}

// ============================================================================
// Component
// ============================================================================

export function ClaimAuthFlow({
  contactEmail,
  existingAccount,
  onAuthenticated,
  onBack,
}: ClaimAuthFlowProps) {
  const colors = useThemeColors();
  const router = useRouter();
  const { signIn, signUp } = useAuth();
  const { alert, error: showError } = useAlert();

  // Form state
  const [mode, setMode] = useState<'login' | 'signup'>(existingAccount ? 'login' : 'signup');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // ============================================================================
  // Handlers
  // ============================================================================

  const loginMutation = useMutation({
    mutationFn: async () => {
      const { error } = await signIn(email.trim(), password);
      if (error) {
        showError('Sign In Failed', sanitizeAuthError(error.message));
        return;
      }
      onAuthenticated();
    },
    onError: (err) => {
      const { message, isCancelled } = handleError(err, { context: 'ClaimAuthFlow.handleLogin' });
      if (!isCancelled) showError('Error', message);
    },
  });

  const signupMutation = useMutation({
    mutationFn: async () => {
      const { error, needsEmailConfirmation } = await signUp(email.trim(), password, 'startup');
      if (error) {
        showError('Sign Up Failed', sanitizeAuthError(error.message));
        return;
      }

      if (needsEmailConfirmation) {
        alert(
          'Check Your Email',
          'We sent a confirmation link to your email address. Please click the link to activate your account, then return here to sign in.'
        );
        setMode('login');
        return;
      }

      onAuthenticated();
    },
    onError: (err) => {
      const { message, isCancelled } = handleError(err, { context: 'ClaimAuthFlow.handleSignup' });
      if (!isCancelled) showError('Error', message);
    },
  });

  const isLoading = loginMutation.isPending || signupMutation.isPending;

  const handleLogin = () => {
    if (!email.trim() || !password.trim()) {
      alert('Missing Information', 'Please enter your email and password.');
      return;
    }

    loginMutation.mutate();
  };

  const handleSignup = () => {
    if (!fullName.trim()) {
      alert('Missing Information', 'Please enter your full name.');
      return;
    }
    if (!email.trim()) {
      alert('Missing Information', 'Please enter your email address.');
      return;
    }
    if (!password.trim()) {
      alert('Missing Information', 'Please enter a password.');
      return;
    }

    const passwordCheck = validatePassword(password);
    if (!passwordCheck.valid) {
      setPasswordError(passwordCheck.message);
      return;
    }
    setPasswordError(null);

    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }

    if (!termsAccepted) {
      alert('Terms Required', 'Please accept the Terms of Service and Privacy Policy to continue.');
      return;
    }

    signupMutation.mutate();
  };

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={{ flex: 1 }}
    >
      <Animated.View entering={FadeInUp.delay(100).duration(500)}>
        {/* Back Button */}
        <AnimatedPressable
          onPress={onBack}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 6,
            marginBottom: 20,
          }}
        >
          <ChevronLeft size={20} color={colors.primary[600]} />
          <Text
            className="text-sm font-semibold"
            style={{ color: colors.primary[600] }}
          >
            Back to Score Preview
          </Text>
        </AnimatedPressable>

        {/* Card */}
        <View
          style={{
            backgroundColor: colors.surface.primary,
            borderRadius: 24,
            padding: 24,
            shadowColor: '#000',
            shadowOpacity: 0.06,
            shadowRadius: 16,
            shadowOffset: { width: 0, height: 4 },
            elevation: 4,
          }}
        >
          {/* Header */}
          <Text
            className="text-xl font-bold"
            style={{
              color: colors.text.primary,
              marginBottom: 6,
            }}
          >
            {mode === 'login' ? 'Welcome Back' : 'Create Your Account'}
          </Text>
          <Text
            className="text-sm"
            style={{
              color: colors.text.secondary,
              marginBottom: 8,
              lineHeight: 20,
            }}
          >
            {mode === 'login'
              ? 'Log in to claim your score and access your dashboard.'
              : 'Create an account to see your full score and unlock all features.'}
          </Text>

          {/* Masked email hint */}
          <View
            style={{
              backgroundColor: colors.primary[50],
              borderRadius: 12,
              paddingHorizontal: 14,
              paddingVertical: 10,
              marginBottom: 20,
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Mail size={16} color={colors.primary[600]} />
            <Text
              className="text-sm font-medium"
              style={{
                color: colors.primary[700],
                flex: 1,
              }}
            >
              Your deck was submitted with {contactEmail}
            </Text>
          </View>

          {/* Full Name (signup only) */}
          {mode === 'signup' && (
            <Animated.View entering={FadeInUp.delay(150).duration(400)}>
              <Text
                className="text-sm font-medium"
                style={{
                  color: colors.text.secondary,
                  marginBottom: 6,
                }}
              >
                Full Name
              </Text>
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  borderRadius: 12,
                  paddingHorizontal: 14,
                  borderWidth: 1,
                  borderColor: colors.border.light,
                  backgroundColor: colors.surface.secondary,
                  marginBottom: 14,
                }}
              >
                <User size={18} color={colors.text.tertiary} />
                <TextInput
                  className="text-base"
                  style={{
                    flex: 1,
                    paddingVertical: 14,
                    paddingLeft: 10,
                    color: colors.text.primary,
                  }}
                  placeholder="Enter your full name"
                  placeholderTextColor={colors.text.tertiary}
                  value={fullName}
                  onChangeText={setFullName}
                  autoCapitalize="words"
                  autoComplete="name"
                />
              </View>
            </Animated.View>
          )}

          {/* Email */}
          <Text
            className="text-sm font-medium"
            style={{
              color: colors.text.secondary,
              marginBottom: 6,
            }}
          >
            Email Address
          </Text>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              borderRadius: 12,
              paddingHorizontal: 14,
              borderWidth: 1,
              borderColor: colors.border.light,
              backgroundColor: colors.surface.secondary,
              marginBottom: 14,
            }}
          >
            <Mail size={18} color={colors.text.tertiary} />
            <TextInput
              className="text-base"
              style={{
                flex: 1,
                paddingVertical: 14,
                paddingLeft: 10,
                color: colors.text.primary,
              }}
              placeholder="Enter your email"
              placeholderTextColor={colors.text.tertiary}
              value={email}
              onChangeText={setEmail}
              keyboardType="email-address"
              autoCapitalize="none"
              autoComplete="email"
            />
          </View>

          {/* Password */}
          <Text
            className="text-sm font-medium"
            style={{
              color: colors.text.secondary,
              marginBottom: 6,
            }}
          >
            Password
          </Text>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              borderRadius: 12,
              paddingHorizontal: 14,
              borderWidth: 1,
              borderColor: passwordError ? colors.error : colors.border.light,
              backgroundColor: colors.surface.secondary,
              marginBottom: passwordError ? 4 : 14,
            }}
          >
            <Lock size={18} color={colors.text.tertiary} />
            <TextInput
              className="text-base"
              style={{
                flex: 1,
                paddingVertical: 14,
                paddingLeft: 10,
                color: colors.text.primary,
              }}
              placeholder={mode === 'signup' ? 'Create a password (min 8 chars)' : 'Enter your password'}
              placeholderTextColor={colors.text.tertiary}
              value={password}
              onChangeText={(text) => {
                setPassword(text);
                if (passwordError) setPasswordError(null);
              }}
              secureTextEntry={!showPassword}
              autoCapitalize="none"
            />
            <Pressable
              onPress={() => setShowPassword(!showPassword)}
              style={{ padding: 6 }}
              accessibilityRole="button"
              accessibilityLabel={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <EyeOff size={18} color={colors.text.tertiary} />
              ) : (
                <Eye size={18} color={colors.text.tertiary} />
              )}
            </Pressable>
          </View>
          {passwordError && (
            <Text
              className="text-xs"
              style={{
                color: colors.error,
                marginBottom: 14,
                marginLeft: 2,
              }}
            >
              {passwordError}
            </Text>
          )}

          {/* Confirm Password (signup only) */}
          {mode === 'signup' && (
            <Animated.View entering={FadeInUp.delay(200).duration(400)}>
              <Text
                className="text-sm font-medium"
                style={{
                  color: colors.text.secondary,
                  marginBottom: 6,
                }}
              >
                Confirm Password
              </Text>
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  borderRadius: 12,
                  paddingHorizontal: 14,
                  borderWidth: 1,
                  borderColor: colors.border.light,
                  backgroundColor: colors.surface.secondary,
                  marginBottom: 14,
                }}
              >
                <Lock size={18} color={colors.text.tertiary} />
                <TextInput
                  className="text-base"
                  style={{
                    flex: 1,
                    paddingVertical: 14,
                    paddingLeft: 10,
                    color: colors.text.primary,
                  }}
                  placeholder="Confirm your password"
                  placeholderTextColor={colors.text.tertiary}
                  value={confirmPassword}
                  onChangeText={setConfirmPassword}
                  secureTextEntry={!showPassword}
                  autoCapitalize="none"
                />
              </View>
            </Animated.View>
          )}

          {/* Forgot Password (login only) */}
          {mode === 'login' && (
            <Pressable
              onPress={() => router.push('/forgot-password')}
              style={{ alignSelf: 'flex-end', marginBottom: 16 }}
              accessibilityRole="link"
              accessibilityLabel="Forgot password"
            >
              <Text
                className="text-sm font-semibold"
                style={{ color: colors.primary[600] }}
              >
                Forgot Password?
              </Text>
            </Pressable>
          )}

          {/* Terms Checkbox (signup only) */}
          {mode === 'signup' && (
            <Animated.View entering={FadeInUp.delay(250).duration(400)}>
              <Pressable
                onPress={() => setTermsAccepted(!termsAccepted)}
                style={{
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  gap: 10,
                  marginBottom: 20,
                }}
                accessibilityRole="checkbox"
                accessibilityState={{ checked: termsAccepted }}
              >
                <View
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 6,
                    borderWidth: 2,
                    borderColor: termsAccepted ? colors.primary[600] : colors.border.medium,
                    backgroundColor: termsAccepted ? colors.primary[600] : 'transparent',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginTop: 1,
                  }}
                >
                  {termsAccepted && <Check size={14} color="#fff" />}
                </View>
                <Text
                  className="text-sm"
                  style={{
                    flex: 1,
                    color: colors.text.secondary,
                    lineHeight: 19,
                  }}
                >
                  I agree to the Terms of Service and Privacy Policy
                </Text>
              </Pressable>
            </Animated.View>
          )}

          {/* Submit Button */}
          <AnimatedPressable
            onPress={mode === 'login' ? handleLogin : handleSignup}
            disabled={isLoading}
            style={{
              borderRadius: 14,
              paddingVertical: 16,
              backgroundColor: isLoading ? colors.border.medium : colors.primary[600],
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 8,
            }}
            accessibilityRole="button"
            accessibilityLabel={mode === 'login' ? 'Sign in' : 'Create account'}
            accessibilityState={{ disabled: isLoading, busy: isLoading }}
          >
            {isLoading ? (
              <ActivityIndicator size="small" color={colors.text.inverse} />
            ) : null}
            <Text
              className="text-base font-semibold"
              style={{ color: colors.text.inverse }}
            >
              {isLoading
                ? 'Please wait...'
                : mode === 'login'
                  ? 'Sign In & Claim'
                  : 'Create Account & Claim'}
            </Text>
          </AnimatedPressable>

          {/* Toggle Mode */}
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: 16,
            }}
          >
            <Text
              className="text-sm"
              style={{ color: colors.text.secondary }}
            >
              {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
            </Text>
            <Pressable
              onPress={() => {
                setMode(mode === 'login' ? 'signup' : 'login');
                setPasswordError(null);
              }}
              accessibilityRole="button"
              accessibilityLabel={mode === 'login' ? 'Switch to sign up' : 'Switch to sign in'}
            >
              <Text
                className="text-sm font-semibold"
                style={{ color: colors.primary[600] }}
              >
                {mode === 'login' ? 'Sign Up' : 'Sign In'}
              </Text>
            </Pressable>
          </View>

          {/* Note */}
          <Text
            className="text-xs"
            style={{
              color: colors.text.tertiary,
              textAlign: 'center',
              marginTop: 16,
              lineHeight: 18,
            }}
          >
            This email was used when your deck was submitted. It must match to claim this score.
          </Text>
        </View>
      </Animated.View>
    </KeyboardAvoidingView>
  );
}
